import React from 'react'
import PropTypes from 'prop-types'
import ReactTooltip from 'react-tooltip'

// hixme-ui
import Container from '@hixme-ui/container'
import Icon from '@hixme-ui/icon'
import Text from '@hixme-ui/text'
import Button from '@hixme-ui/button'

import { getBundleLetter } from '@hixme/bundle-utils'

import { PersonBenefitPrice } from 'store/modules/persons/components'

import { MAX_FAVORITES } from '../../constants'

import { BundleAccordion } from './components'

// Importing these containers directly to avoid circular dependencies problem
import AddBenefitButtonContainer from '../../containers/AddBenefitButtonContainer'
import CompareNavContainer from '../../../../containers/CompareNavContainer'
import { currencyFormatter } from '../../../../../../Expenses/utils/expenses'
import ICHRATooltip from '../../../../components/BundlePlanTypeTooltips/components/ICHRATooltip/ICHRATooltip'
import ProviderNote from './components/ProviderNote/ProviderNote'

const BundleCard = ({
  bundle,
  currentGrpState,
  coveredPeopleLabel,
  onToggleFavorite,
  groupFavoriteCount,
  providers,
}) => {
  const {
    BundlePublicKey,
    // BundleLevel,
    CarrierName,
    HealthPlanId,
    // HixmeValue,
    IsHsa,
    PlanType,
    PlanName,
    MetalLevel,
    sections,
    bundlePrice,
    ICHRAPrice,
    favorite,
  } = bundle

  return (
    <div>
      <Container background="white" noPadding shadow>
        <Container flex justifyContent="center" alignItems="center" noPadding height="30px">
          {MetalLevel === 'Catastrophic' && (
            <Text primary small>
              Only available for persons aged 30 and under
            </Text>
          )}
        </Container>

        <Container flex noPadding direction="column" alignItems="center">
          <PersonBenefitPrice price={bundlePrice} label={coveredPeopleLabel} />
        </Container>

        <Container
          flex
          direction="column"
          alignItems="center"
          margin="0 0 20px 0"
          padding="0 10px 0 10px"
        >
          <Container flex noPadding direction="column" alignItems="center">
            <Container flex noPadding height="60px">
              <Text p center thin large>
                {CarrierName}
              </Text>
            </Container>
            {CarrierName === 'Blue Shield' && currentGrpState === 'CA' && (
              <Container flex noPadding>
                {' '}
                <Text p center thin small>
                  <a href="https://www.blueshieldca.com/fad/home" target={'_blank'}>
                    visit website
                  </a>
                </Text>
              </Container>
            )}

            <Container flex noPadding direction="column" style={{ gap: '15px' }}>
              <span
                data-tip
                data-for={`${PlanType}${BundlePublicKey}`}
                style={{ textAlign: 'center' }}
              >
                <Text style={{ marginBottom: ICHRAPrice ? 0 : '14px' }}>
                  {PlanName} {IsHsa && '(HSA Eligible)'}
                </Text>
              </span>

              {ICHRAPrice && (
                <span data-tip data-for={`ICHRA-${ICHRAPrice}`}>
                  <Container flex noPadding alignItems="center">
                    <Icon dollar primary />
                    <Text dashed p center>
                      ICHRA Expense allowance:{' '}
                      {currencyFormatter(ICHRAPrice, {
                        maximumSignificantDigits: 1,
                      })}
                    </Text>
                  </Container>
                  <ICHRATooltip tooltipId={`ICHRA-${ICHRAPrice}`} />
                </span>
              )}
              {providers &&
                providers.map((provider) => (
                  <ProviderNote
                    key={provider.npi}
                    provider={provider}
                    bundleId={BundlePublicKey}
                    visible={provider.isSelected}
                  />
                ))}
            </Container>
          </Container>
        </Container>

        <BundleAccordion Bundle={bundle} HealthPlanId={HealthPlanId} Sections={sections} />
        <div style={{ borderTop: '1px solid #DBE2E8' }}>
          <Container
            flex
            height="100%"
            direction="column"
            justifyContent="flex-end"
            padding="0 20px 20px 20px"
          >
            <Container noPadding margin="10px 0">
              <Text smaller light>
                {bundle.HealthPlanId} {getBundleLetter(bundle)}
              </Text>
            </Container>
            {groupFavoriteCount === MAX_FAVORITES && (
              <ReactTooltip id="maxReached" effect="solid">
                <span>Compare maximum of {MAX_FAVORITES} reached</span>
              </ReactTooltip>
            )}
            <Container flex noPadding justifyContent="flex-end" height="40px" alignItems="center">
              {favorite && <CompareNavContainer />}
            </Container>

            <Container flex noPadding>
              <a
                style={{ marginRight: '10px', width: '100%' }}
                href={bundle.SbcLinks[0].Url}
                rel="noopener noreferrer"
                target="_blank"
              >
                <Button outline light width="100%" style={{ padding: '5px' }}>
                  <Container flex noPadding alignItems="center">
                    <Icon newWindow light size={1.1} />
                    <Text style={{ marginLeft: '10px' }} light thin>
                      Benefits summary
                    </Text>
                  </Container>
                </Button>
              </a>
              <div data-tip data-for={favorite ? null : 'maxReached'}>
                <Button
                  outline
                  light
                  onClick={() => onToggleFavorite(BundlePublicKey)}
                  style={{ padding: '5px' }}
                >
                  <Container flex noPadding alignItems="center">
                    <Icon
                      heart={favorite}
                      heartOutline={!favorite}
                      red={favorite}
                      light={!favorite}
                      size={1.1}
                    />
                    <div style={{ marginLeft: '10px' }}>Compare</div>
                  </Container>
                </Button>
              </div>
            </Container>

            <Container flex noPadding margin="10px 0 0 0" justifyContent="center">
              <AddBenefitButtonContainer bundleId={BundlePublicKey} />
            </Container>
          </Container>
        </div>
      </Container>
    </div>
  )
}

BundleCard.propTypes = {
  bundle: PropTypes.object,
  currentGrpState: PropTypes.string,
  groupFavoriteCount: PropTypes.number,
  coveredPeopleLabel: PropTypes.string,
  providers: PropTypes.array,
  onToggleFavorite: PropTypes.func,
}

BundleCard.defaultProps = {
  bundle: {
    BundleBenefits: [],
    BundlePublicKey: '0',
    CarrierName: '',
    CoveredHospitals: [],
    HsaPrice: 0,
    IsHsa: false,
    PlanName: '',
    Price: 0,
    ICHRAPrice: null,
    bundleCoreBenefits: [],
    bundlePrice: 0,
    favorite: false,
    groupPersons: [],
  },
  currentGrpState: '',
  coveredPeopleLabel: '',
  providers: [],
  isProcessing: false,
  stackLength: 0,
  currentBundleIndex: 0,
  groupFavoriteCount: 0,

  onAddToCart: () => {},
  onRemoveFromCart: () => {},
  onToggleFavorite: () => {},
}

BundleCard.displayName = 'BundleCard'

export default BundleCard
